import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Calculator, FileText, DollarSign, Clock, Users, Settings, ChevronRight, CheckCircle, AlertCircle } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const CalculatorContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  overflow: hidden;
  max-width: 900px;
  margin: 0 auto;
`;

const StepIndicator = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  padding: 20px;
  display: flex;
  justify-content: center;
  gap: 20px;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.active ? 'white' : 'rgba(255,255,255,0.6)'};
  font-weight: ${props => props.active ? '600' : '400'};
  font-size: 14px;
`;

const StepNumber = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.active ? 'white' : 'rgba(255,255,255,0.2)'};
  color: ${props => props.active ? '#3b82f6' : 'rgba(255,255,255,0.8)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

const FormSection = styled.div`
  padding: 30px;
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`;

const OptionCard = styled.div`
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.selected ? '#f0f9ff' : 'white'};
  
  &:hover {
    border-color: ${props => props.selected ? '#3b82f6' : '#9ca3af'};
    background: ${props => props.selected ? '#f0f9ff' : '#f9fafb'};
  }
`;

const OptionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 5px 0;
`;

const OptionDescription = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 10px 0;
  line-height: 1.4;
`;

const OptionPrice = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.selected ? '#3b82f6' : '#374151'};
`;

const SliderContainer = styled.div`
  margin: 20px 0;
`;

const SliderLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
`;

const SliderWrapper = styled.div`
  position: relative;
  margin: 20px 0;
`;

const Slider = styled.input`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    transition: background 0.2s;
    
    &:hover {
      background: #2563eb;
    }
  }
  
  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: none;
  }
`;

const SliderValue = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #3b82f6;
  margin-top: 10px;
`;

const EstimatePanel = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 25px;
  margin: 20px 0;
`;

const EstimateTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EstimateItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: 2px solid #3b82f6;
    font-weight: 600;
    color: #3b82f6;
    margin-top: 10px;
    padding-top: 15px;
  }
`;

const EstimateLabel = styled.span`
  font-size: 14px;
  color: #374151;
`;

const EstimateValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 15px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${props => props.primary ? '#3b82f6' : '#6b7280'};
  color: white;
  
  &:hover {
    background: ${props => props.primary ? '#2563eb' : '#4b5563'};
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 15px 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  font-size: 13px;
  color: #374151;
`;

const InfoBox = styled.div`
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 15px;
  margin: 20px 0;
  font-size: 13px;
  color: #92400e;
  display: flex;
  align-items: start;
  gap: 10px;
`;

function QuoteCalculator() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selections, setSelections] = useState({
    projectType: '',
    complexity: '',
    features: [],
    timeline: 4,
    teamSize: 2,
    maintenance: false
  });
  const [estimate, setEstimate] = useState(0);

  const projectTypes = {
    website: {
      title: '웹사이트',
      description: '반응형 웹사이트, 랜딩페이지',
      basePrice: 300,
      features: ['반응형 디자인', '기본 SEO', 'CMS 연동']
    },
    ecommerce: {
      title: '쇼핑몰',
      description: '온라인 상거래 사이트',
      basePrice: 800,
      features: ['상품관리', '결제시스템', '주문관리', '회원관리']
    },
    webapp: {
      title: '웹앱',
      description: '복잡한 기능의 웹 애플리케이션',
      basePrice: 1200,
      features: ['사용자 인증', '데이터베이스', 'API 개발', '관리자 패널']
    },
    mobile: {
      title: '모바일 앱',
      description: 'iOS/Android 네이티브 앱',
      basePrice: 2000,
      features: ['크로스플랫폼', '푸시알림', '앱스토어 등록', '업데이트 지원']
    }
  };

  const complexityLevels = {
    simple: {
      title: '단순',
      description: '기본 기능만 포함',
      multiplier: 1.0,
      features: ['기본 UI/UX', '표준 기능']
    },
    medium: {
      title: '보통',
      description: '일반적인 수준의 기능',
      multiplier: 1.5,
      features: ['커스텀 디자인', '중급 기능', '3rd Party 연동']
    },
    complex: {
      title: '복잡',
      description: '고급 기능 및 커스터마이징',
      multiplier: 2.2,
      features: ['고급 UI/UX', '복잡한 로직', '다중 연동', '고급 보안']
    }
  };

  const additionalFeatures = [
    { id: 'admin', name: '관리자 페이지', price: 200 },
    { id: 'api', name: 'API 개발', price: 300 },
    { id: 'payment', name: '결제 시스템', price: 400 },
    { id: 'chat', name: '실시간 채팅', price: 250 },
    { id: 'analytics', name: '데이터 분석', price: 180 },
    { id: 'security', name: '보안 강화', price: 220 }
  ];

  useEffect(() => {
    calculateEstimate();
  }, [selections]);

  const calculateEstimate = () => {
    let total = 0;
    
    if (selections.projectType && projectTypes[selections.projectType]) {
      total += projectTypes[selections.projectType].basePrice;
    }
    
    if (selections.complexity && complexityLevels[selections.complexity]) {
      total *= complexityLevels[selections.complexity].multiplier;
    }
    
    // 추가 기능 비용
    selections.features.forEach(featureId => {
      const feature = additionalFeatures.find(f => f.id === featureId);
      if (feature) {
        total += feature.price;
      }
    });
    
    // 타임라인 조정 (짧을수록 비용 증가)
    if (selections.timeline < 4) {
      total *= 1.3; // 30% 할증
    } else if (selections.timeline > 8) {
      total *= 0.9; // 10% 할인
    }
    
    // 팀 크기 조정
    if (selections.teamSize > 3) {
      total *= (1 + (selections.teamSize - 3) * 0.2);
    }
    
    // 유지보수 포함
    if (selections.maintenance) {
      total *= 1.15;
    }
    
    setEstimate(Math.round(total));
  };

  const handleSelection = (category, value) => {
    setSelections(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleFeatureToggle = (featureId) => {
    setSelections(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(id => id !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const nextStep = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const getEstimateBreakdown = () => {
    const items = [];
    
    if (selections.projectType && projectTypes[selections.projectType]) {
      const basePrice = projectTypes[selections.projectType].basePrice;
      items.push({
        label: `${projectTypes[selections.projectType].title} (기본)`,
        value: `${basePrice}만원`
      });
      
      if (selections.complexity && complexityLevels[selections.complexity]) {
        const multiplier = complexityLevels[selections.complexity].multiplier;
        if (multiplier !== 1.0) {
          items.push({
            label: `복잡도 조정 (${complexityLevels[selections.complexity].title})`,
            value: `x ${multiplier}`
          });
        }
      }
    }
    
    selections.features.forEach(featureId => {
      const feature = additionalFeatures.find(f => f.id === featureId);
      if (feature) {
        items.push({
          label: feature.name,
          value: `+${feature.price}만원`
        });
      }
    });
    
    if (selections.timeline < 4) {
      items.push({
        label: '급행 개발 (할증)',
        value: '+30%'
      });
    }
    
    if (selections.maintenance) {
      items.push({
        label: '6개월 유지보수 포함',
        value: '+15%'
      });
    }
    
    return items;
  };

  const renderStep = () => {
    switch(currentStep) {
      case 1:
        return (
          <FormSection>
            <SectionTitle>
              <FileText size={18} />
              프로젝트 유형 선택
            </SectionTitle>
            <OptionsGrid>
              {Object.entries(projectTypes).map(([key, type]) => (
                <OptionCard
                  key={key}
                  selected={selections.projectType === key}
                  onClick={() => handleSelection('projectType', key)}
                >
                  <OptionTitle>{type.title}</OptionTitle>
                  <OptionDescription>{type.description}</OptionDescription>
                  <OptionPrice selected={selections.projectType === key}>
                    기본 {type.basePrice}만원~
                  </OptionPrice>
                  <FeatureList>
                    {type.features.map((feature, index) => (
                      <FeatureItem key={index}>
                        <CheckCircle size={12} style={{color: '#10b981'}} />
                        {feature}
                      </FeatureItem>
                    ))}
                  </FeatureList>
                </OptionCard>
              ))}
            </OptionsGrid>
          </FormSection>
        );
        
      case 2:
        return (
          <FormSection>
            <SectionTitle>
              <Settings size={18} />
              복잡도 및 기능 선택
            </SectionTitle>
            <div style={{marginBottom: '30px'}}>
              <h4 style={{marginBottom: '15px', color: '#374151'}}>프로젝트 복잡도</h4>
              <OptionsGrid>
                {Object.entries(complexityLevels).map(([key, level]) => (
                  <OptionCard
                    key={key}
                    selected={selections.complexity === key}
                    onClick={() => handleSelection('complexity', key)}
                  >
                    <OptionTitle>{level.title}</OptionTitle>
                    <OptionDescription>{level.description}</OptionDescription>
                    <OptionPrice selected={selections.complexity === key}>
                      기본가 x {level.multiplier}
                    </OptionPrice>
                    <FeatureList>
                      {level.features.map((feature, index) => (
                        <FeatureItem key={index}>
                          <CheckCircle size={12} style={{color: '#10b981'}} />
                          {feature}
                        </FeatureItem>
                      ))}
                    </FeatureList>
                  </OptionCard>
                ))}
              </OptionsGrid>
            </div>
            
            <div>
              <h4 style={{marginBottom: '15px', color: '#374151'}}>추가 기능 (선택사항)</h4>
              <OptionsGrid>
                {additionalFeatures.map(feature => (
                  <OptionCard
                    key={feature.id}
                    selected={selections.features.includes(feature.id)}
                    onClick={() => handleFeatureToggle(feature.id)}
                  >
                    <OptionTitle>{feature.name}</OptionTitle>
                    <OptionPrice selected={selections.features.includes(feature.id)}>
                      +{feature.price}만원
                    </OptionPrice>
                  </OptionCard>
                ))}
              </OptionsGrid>
            </div>
          </FormSection>
        );
        
      case 3:
        return (
          <FormSection>
            <SectionTitle>
              <Clock size={18} />
              개발 일정 및 팀 구성
            </SectionTitle>
            
            <SliderContainer>
              <SliderLabel>개발 기간 (주)</SliderLabel>
              <SliderWrapper>
                <Slider
                  type="range"
                  min="2"
                  max="16"
                  value={selections.timeline}
                  onChange={(e) => handleSelection('timeline', parseInt(e.target.value))}
                />
                <SliderValue>
                  {selections.timeline}주
                  {selections.timeline < 4 && ' (급행 개발 +30%)'}
                  {selections.timeline > 8 && ' (여유 개발 -10%)'}
                </SliderValue>
              </SliderWrapper>
            </SliderContainer>
            
            <SliderContainer>
              <SliderLabel>팀 크기 (명)</SliderLabel>
              <SliderWrapper>
                <Slider
                  type="range"
                  min="1"
                  max="8"
                  value={selections.teamSize}
                  onChange={(e) => handleSelection('teamSize', parseInt(e.target.value))}
                />
                <SliderValue>{selections.teamSize}명</SliderValue>
              </SliderWrapper>
            </SliderContainer>
            
            <OptionCard
              selected={selections.maintenance}
              onClick={() => handleSelection('maintenance', !selections.maintenance)}
              style={{marginTop: '20px'}}
            >
              <OptionTitle>6개월 유지보수 포함</OptionTitle>
              <OptionDescription>
                버그 수정, 소규모 기능 추가, 기술 지원 포함
              </OptionDescription>
              <OptionPrice selected={selections.maintenance}>+15%</OptionPrice>
            </OptionCard>
          </FormSection>
        );
        
      case 4:
        return (
          <FormSection>
            <SectionTitle>
              <DollarSign size={18} />
              견적 결과
            </SectionTitle>
            
            <EstimatePanel>
              <EstimateTitle>
                <Calculator size={18} />
                예상 견적 내역
              </EstimateTitle>
              
              {getEstimateBreakdown().map((item, index) => (
                <EstimateItem key={index}>
                  <EstimateLabel>{item.label}</EstimateLabel>
                  <EstimateValue>{item.value}</EstimateValue>
                </EstimateItem>
              ))}
              
              <EstimateItem>
                <EstimateLabel style={{fontSize: '16px'}}>총 예상 비용</EstimateLabel>
                <EstimateValue style={{fontSize: '18px', color: '#3b82f6'}}>
                  {estimate.toLocaleString()}만원
                </EstimateValue>
              </EstimateItem>
            </EstimatePanel>
            
            <InfoBox>
              <AlertCircle size={16} />
              <div>
                이 견적은 대략적인 예상 비용입니다. 정확한 견적을 위해서는 상세한 요구사항 분석이 필요합니다.
                실제 비용은 ±20% 범위에서 달라질 수 있습니다.
              </div>
            </InfoBox>
            
            <ActionButtons>
              <ActionButton primary onClick={() => window.location.href = '/quote-request'}>
                <FileText size={16} />
                정식 견적 요청하기
              </ActionButton>
              <ActionButton onClick={() => {
                setCurrentStep(1);
                setSelections({
                  projectType: '',
                  complexity: '',
                  features: [],
                  timeline: 4,
                  teamSize: 2,
                  maintenance: false
                });
              }}>
                다시 계산하기
              </ActionButton>
            </ActionButtons>
          </FormSection>
        );
        
      default:
        return null;
    }
  };

  return (
    <ContentLayout 
      title="견적 계산기"
      subtitle="프로젝트 요구사항을 선택하시면 예상 견적을 자동으로 계산해드립니다"
    >
      <Container>
        <CalculatorContainer>
          <StepIndicator>
            <Step active={currentStep >= 1}>
              <StepNumber active={currentStep >= 1}>1</StepNumber>
              프로젝트 유형
            </Step>
            <ChevronRight size={16} style={{color: 'rgba(255,255,255,0.6)'}} />
            <Step active={currentStep >= 2}>
              <StepNumber active={currentStep >= 2}>2</StepNumber>
              복잡도 & 기능
            </Step>
            <ChevronRight size={16} style={{color: 'rgba(255,255,255,0.6)'}} />
            <Step active={currentStep >= 3}>
              <StepNumber active={currentStep >= 3}>3</StepNumber>
              일정 & 팀
            </Step>
            <ChevronRight size={16} style={{color: 'rgba(255,255,255,0.6)'}} />
            <Step active={currentStep >= 4}>
              <StepNumber active={currentStep >= 4}>4</StepNumber>
              견적 결과
            </Step>
          </StepIndicator>
          
          {renderStep()}
          
          {currentStep < 4 && (
            <FormSection>
              <ActionButtons>
                {currentStep > 1 && (
                  <ActionButton onClick={prevStep}>
                    이전 단계
                  </ActionButton>
                )}
                <ActionButton 
                  primary 
                  onClick={nextStep}
                  disabled={
                    (currentStep === 1 && !selections.projectType) ||
                    (currentStep === 2 && !selections.complexity)
                  }
                >
                  다음 단계
                  <ChevronRight size={16} />
                </ActionButton>
              </ActionButtons>
            </FormSection>
          )}
          
          {estimate > 0 && (
            <FormSection style={{background: '#f0f9ff', textAlign: 'center'}}>
              <div style={{fontSize: '14px', color: '#6b7280', marginBottom: '5px'}}>
                현재 예상 견적
              </div>
              <div style={{fontSize: '24px', fontWeight: '700', color: '#3b82f6'}}>
                {estimate.toLocaleString()}만원
              </div>
            </FormSection>
          )}
        </CalculatorContainer>
      </Container>
    </ContentLayout>
  );
}

export default QuoteCalculator;